import * as Sentry from '@sentry/react';

/**
 * Service managing cart addition animations
 */
export class CartAnimationService {
  constructor() {
    this.defaultAnimationDuration = 750;
    this.shakeDuration = 2400;
  }

  /**
   * Calculate the offset of a DOM element
   * @param {HTMLElement} element - The element to calculate offset for
   * @returns {DOMRect|null} Object containing element coordinates
   */
  getElementOffset(element) {

    if(!element){
      console.warn('getElementOffset: element is null');
      return null;
    }

    if (!element.isConnected) {
      console.warn('getElementOffset: element is not connected to DOM');
      return null;
    }

    if (typeof element.getBoundingClientRect !== 'function') {
      console.warn('getElementOffset: getBoundingClientRect method not available');
      return null;
    }

    try {
      const rect = element.getBoundingClientRect();

      if(!rect){
        console.warn('getElementOffset: getBoundingClientRect returned null');
        return null;
      }

      return rect;
    } catch (error) {
      console.error('getElementOffset error:', error);
      Sentry.captureException(error);
      return null;
    }  }

  /**
   * Calculate translation values for animation
   * @param {DOMRect} sourceOffset - Source element offset
   * @param {DOMRect} targetOffset - Target element offset
   * @returns {Object} Object containing translateX and translateY
   */
  calculateTranslation(sourceOffset, targetOffset) {
    if (!sourceOffset || !targetOffset) {
      return { translateX: 0, translateY: 0 };
    }

    const translateX = sourceOffset.left - targetOffset.left > 10 
      ? -(sourceOffset.left - targetOffset.left) 
      : 0;
    
    const translateY = sourceOffset.top - targetOffset.top > 10 
      ? -(sourceOffset.top - targetOffset.top) 
      : 0;

    return { translateX, translateY };
  }

  /**
   * Apply CSS styles for animation
   * @param {HTMLElement} animElement - Element to animate
   * @param {DOMRect} productOffset - Product offset
   * @param {Object} translation - Translation values
   */
  applyAnimationStyles(animElement, productOffset, translation) {
    if (!animElement || !productOffset) {
      console.warn('applyAnimationStyles: missing required elements');
      return;
    }

    try {
      animElement.style.top = `${productOffset.top}px`;
      animElement.style.left = `${productOffset.left}px`;
      
      animElement.style.setProperty('--translate-x', `${translation.translateX}px`);
      animElement.style.setProperty('--translate-y', `${translation.translateY}px`);
    } catch (error) {
      console.error('Error applying animation styles:', error);
      Sentry.captureException(error);
    }
  }

  /**
   * Setup product to cart animation
   * @param {string} productElementId - Product element ID
   * @param {string} cartElementId - Cart element ID
   * @param {HTMLElement} animElement - Animation element
   */
  setupProductToCartAnimation(productElementId, cartElementId, animElement) {
    try {
      const productElement = document.getElementById(productElementId);
      const cartElement = document.getElementById(cartElementId);

      if (!productElement) {
        console.warn(`Product element with id "${productElementId}" not found`);
        return;
      }

      if (!cartElement) {
        console.warn(`Cart element with id "${cartElementId}" not found`);
        return;
      }

      const productOffset = this.getElementOffset(productElement);
      const cartOffset = this.getElementOffset(cartElement);

      if (!productOffset || !cartOffset) {
        return;
      }

      const translation = this.calculateTranslation(productOffset, cartOffset);
      this.applyAnimationStyles(animElement, productOffset, translation);

    } catch (error) {
      console.error('setupProductToCartAnimation error:', error);
      Sentry.captureException(error);
    }
  }

  /**
   * Handle cart shake animation
   * @param {string} footerCartId - Footer cart element ID
   * @param {Function} onAnimationComplete - Callback to execute when animation completes
   */
  animateCartShake(footerCartId, onAnimationComplete) {
    const footerCart = document.getElementById(footerCartId);
    
    if (!footerCart) {
      console.warn(`Footer cart element with id "${footerCartId}" not found`);
      if (onAnimationComplete) onAnimationComplete();
      return;
    }

    try {
      footerCart.classList.remove('shake');

      setTimeout(() => {
        footerCart.classList.add('shake');
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, this.defaultAnimationDuration);

    } catch (error) {
      console.error('animateCartShake error:', error);
      Sentry.captureException(error);
      if (onAnimationComplete) onAnimationComplete();
    }
  }

  /**
   * Control navbar visibility during animation
   * @param {Function} setIsNavbarHidden - Function to control navbar visibility
   * @param {boolean} hidden - Visibility state
   */
  toggleNavbar(setIsNavbarHidden, hidden) {
    if (typeof setIsNavbarHidden === 'function') {
      setIsNavbarHidden(hidden);
    }
  }
}

export const cartAnimationService = new CartAnimationService(); 